import { useEffect, useMemo, useState } from 'react';
import Modal from './ui/Modal';
import { getCommunicationPRIsME } from '../data';
import { TabGroup, TabList, Tab, TabPanel } from './ui/Tabs';
import { Loader } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  getGouvernancePrisme2Tasks,
  saveGouvernancePrisme2Tasks,
  type GouvernancePrisme2TaskRow,
} from '../services/gouvernancePrisme2TasksService';

// Types pour le typage explicite
interface Point {
  id: string;
  titre: string;
  contenu: string;
  dureeMinutes: number;
  graphiques: string[];
  concepts: string[];
}

interface OrdreDuJourItem {
  id: string;
  numero: number;
  titre: string;
  description: string;
  presentateur: any;
  dureeMinutes: number;
  niveau: string;
  statut: string;
  points: Point[];
}

interface Communication {
  id: string;
  titre: string;
  date: string;
  heure: string;
  dureeMinutes: number;
  lieu: string;
  objectifPrincipal: string;
  statut: string;
  participants: any[];
  ordreDuJour: OrdreDuJourItem[];
}

// Version 2026-04-08 15:08 - Modal spécifique pour Gouvernance PRIsME 2.0
export default function GouvernancePRIsME20Modal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [taskRows, setTaskRows] = useState<GouvernancePrisme2TaskRow[] | null>(null);
  const [tasksMeta, setTasksMeta] = useState<{ canEdit: boolean; updated_at?: string; updated_by_email?: string } | null>(null);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksSaving, setTasksSaving] = useState(false);
  const [tasksError, setTasksError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  /** Un seul tableau : lecture seule par défaut ; les éditeurs activent explicitement la modification */
  const [tasksEditMode, setTasksEditMode] = useState(false);

  // On récupère les données de la communication "gouvernance-prisme-2-0"
  const communication = getCommunicationPRIsME('gouvernance-prisme-2-0') as Communication | null;

  if (!communication) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} size="5xl">
        <div className="p-6 text-center">
          <Loader className="w-6 h-6 animate-spin mx-auto text-gray-400" />
          <p className="mt-3 text-gray-500">Chargement des données...</p>
        </div>
      </Modal>
    );
  }

  const taskColumns = useMemo(
    () =>
      [
        { key: 'objectifs_macro', label: 'Objectifs Macro' },
        { key: 'besoins_specifique', label: 'Besoins spécifique' },
        { key: 'sto', label: 'STO' },
        { key: 'axe_pilier', label: 'axe-pilier' },
        { key: 'thp', label: 'Technologique Humain Processus' },
        { key: 'priorite', label: 'Priorité' },
        { key: 'a', label: 'A' },
        { key: 'r', label: 'R' },
        { key: 'c', label: 'C' },
        { key: 'i', label: 'I' },
        { key: 'date_debut', label: 'Date de début' },
        { key: 'date_validation', label: 'Date de livraison pour validation' },
        { key: 'date_finale', label: 'Date de livraison finale' },
        { key: 'coordination', label: 'Coordination' },
        { key: 'description', label: 'Description de la tâche/activité' },
      ] as const,
    []
  );

  const canEditTasks =
    Boolean(tasksMeta?.canEdit) &&
    Boolean(user?.email) &&
    ['eric.hains@bzinc.ca', 'jean-michel.caouette@bzinc.ca'].includes(user?.email?.toLowerCase?.() || '');

  useEffect(() => {
    if (!isOpen) {
      setTaskRows(null);
      setTasksMeta(null);
      setTasksError(null);
      setDirty(false);
      setTasksEditMode(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const tab = communication.ordreDuJour?.[activeTab];
    if (!tab || tab.id !== 'gouv-3') return;
    // null = pas encore chargé ; [] = chargé (vide ou erreur gérée) — évite la boucle infinie si l’API échoue
    if (taskRows !== null) return;

    let cancelled = false;
    setTasksLoading(true);
    setTasksError(null);
    getGouvernancePrisme2Tasks()
      .then((payload) => {
        if (cancelled) return;
        setTaskRows(Array.isArray(payload.rows) ? payload.rows : []);
        setTasksMeta({
          canEdit: Boolean(payload.canEdit),
          updated_at: payload.updated_at,
          updated_by_email: payload.updated_by_email,
        });
      })
      .catch((e) => {
        if (cancelled) return;
        setTasksError(e instanceof Error ? e.message : 'Erreur chargement tâches');
        setTaskRows([]);
      })
      .finally(() => {
        if (!cancelled) setTasksLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeTab, communication.ordreDuJour, isOpen, taskRows]);

  const onCellChange = (rowIdx: number, key: keyof GouvernancePrisme2TaskRow, value: string) => {
    setTaskRows((prev) => {
      const rows = Array.isArray(prev) ? [...prev] : [];
      const current = rows[rowIdx] || ({} as GouvernancePrisme2TaskRow);
      rows[rowIdx] = { ...current, [key]: value } as GouvernancePrisme2TaskRow;
      return rows;
    });
    setDirty(true);
  };

  const addEmptyRow = () => {
    const empty: GouvernancePrisme2TaskRow = {
      objectifs_macro: '',
      besoins_specifique: '',
      sto: '',
      axe_pilier: '',
      thp: '',
      priorite: '',
      a: '',
      r: '',
      c: '',
      i: '',
      date_debut: '',
      date_validation: '',
      date_finale: '',
      coordination: '',
      description: '',
    };
    setTaskRows((prev) => (Array.isArray(prev) ? [...prev, empty] : [empty]));
    setDirty(true);
  };

  const saveTasks = async () => {
    if (!canEditTasks) return;
    setTasksSaving(true);
    setTasksError(null);
    try {
      const payload = await saveGouvernancePrisme2Tasks(Array.isArray(taskRows) ? taskRows : []);
      setTaskRows(Array.isArray(payload.rows) ? payload.rows : []);
      setTasksMeta({
        canEdit: Boolean(payload.canEdit),
        updated_at: payload.updated_at,
        updated_by_email: payload.updated_by_email,
      });
      setDirty(false);
      setTasksEditMode(false);
    } catch (e) {
      setTasksError(e instanceof Error ? e.message : 'Erreur sauvegarde tâches');
    } finally {
      setTasksSaving(false);
    }
  };

  const cancelTasksEdit = async () => {
    if (!canEditTasks) return;
    setTasksEditMode(false);
    setDirty(false);
    setTasksError(null);
    setTasksLoading(true);
    try {
      const payload = await getGouvernancePrisme2Tasks();
      setTaskRows(Array.isArray(payload.rows) ? payload.rows : []);
      setTasksMeta({
        canEdit: Boolean(payload.canEdit),
        updated_at: payload.updated_at,
        updated_by_email: payload.updated_by_email,
      });
    } catch (e) {
      setTasksError(e instanceof Error ? e.message : 'Erreur rechargement tâches');
    } finally {
      setTasksLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="full">
      <div className="min-h-[calc(100vh-4rem)] bg-gradient-to-br from-slate-50 via-indigo-50/40 to-white p-4 sm:p-6">
        <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-white/70 shadow-sm backdrop-blur">
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-600/10 via-fuchsia-600/5 to-cyan-600/10" />
          <div className="relative flex flex-col gap-4 p-5 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
            <div className="min-w-0">
              <h2 className="text-2xl font-semibold tracking-tight text-slate-900">{communication.titre}</h2>
              <p className="mt-1 text-sm leading-relaxed text-slate-600">{communication.objectifPrincipal}</p>

              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-slate-600">
                <span className="inline-flex items-center rounded-full border border-slate-200 bg-white/70 px-2.5 py-1">
                  <span className="text-slate-500">Date</span>
                  <span className="mx-1 text-slate-300">·</span>
                  <span className="font-medium text-slate-800">{communication.date}</span>
                </span>
                <span className="inline-flex items-center rounded-full border border-slate-200 bg-white/70 px-2.5 py-1">
                  <span className="text-slate-500">Lieu</span>
                  <span className="mx-1 text-slate-300">·</span>
                  <span className="font-medium text-slate-800">{communication.lieu}</span>
                </span>
                <span className="inline-flex items-center rounded-full border border-slate-200 bg-white/70 px-2.5 py-1">
                  <span className="text-slate-500">Durée</span>
                  <span className="mx-1 text-slate-300">·</span>
                  <span className="font-medium text-slate-800">{communication.dureeMinutes} min</span>
                </span>
              </div>
            </div>

            <div className="shrink-0">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-200 bg-white/80 px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-white"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>

        <TabGroup className="mt-5" selectedIndex={activeTab} onChange={setActiveTab}>
          <TabList className="sticky top-0 z-10 mb-4 rounded-2xl border border-slate-200 bg-white/70 p-1 shadow-sm backdrop-blur">
            {communication.ordreDuJour.map((item: OrdreDuJourItem) => (
              <Tab 
                key={item.id}
                className="rounded-xl px-3 py-2 text-sm font-semibold text-slate-600 transition hover:text-slate-900 data-[selected]:bg-gradient-to-r data-[selected]:from-indigo-600 data-[selected]:to-blue-600 data-[selected]:text-white data-[selected]:shadow-sm"
              >
                {item.titre}
              </Tab>
            ))}
          </TabList>

          {communication.ordreDuJour.map((item: OrdreDuJourItem, index: number) => (
            <TabPanel
              key={item.id}
              index={index}
              className={
                item.id === 'gouv-3'
                  ? 'overflow-hidden rounded-2xl border border-slate-200 bg-white/80 shadow-sm backdrop-blur'
                  : 'rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-sm backdrop-blur'
              }
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-slate-900">{item.titre}</h3>
                  <p className="text-sm text-slate-600">{item.description}</p>
                  {item.id === 'gouv-3' && tasksMeta?.updated_at && (
                    <p className="mt-2 text-xs text-slate-500">
                      Dernière mise à jour: {String(tasksMeta.updated_at).slice(0, 19).replace('T', ' ')}
                      {tasksMeta.updated_by_email ? ` · ${tasksMeta.updated_by_email}` : ''}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold bg-indigo-50 text-indigo-700 px-2.5 py-1 rounded-full">
                    {item.niveau}
                  </span>
                  <span className="text-xs font-semibold bg-slate-50 text-slate-700 px-2.5 py-1 rounded-full">
                    {item.dureeMinutes} min
                  </span>
                  {item.id === 'gouv-3' && canEditTasks && !tasksEditMode && (
                    <button
                      type="button"
                      onClick={() => setTasksEditMode(true)}
                      className="ml-2 inline-flex h-8 items-center justify-center rounded-lg border border-indigo-200 bg-indigo-50 px-3 text-xs font-semibold text-indigo-800 shadow-sm transition hover:bg-indigo-100"
                    >
                      Modifier le tableau
                    </button>
                  )}
                  {item.id === 'gouv-3' && canEditTasks && tasksEditMode && (
                    <>
                      <button
                        type="button"
                        onClick={cancelTasksEdit}
                        disabled={tasksSaving || tasksLoading}
                        className="ml-2 inline-flex h-8 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
                      >
                        Annuler
                      </button>
                      <button
                        type="button"
                        onClick={saveTasks}
                        disabled={!dirty || tasksSaving}
                        className="ml-2 inline-flex h-8 items-center justify-center rounded-lg bg-indigo-600 px-3 text-xs font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                        title={dirty ? 'Enregistrer les modifications' : 'Aucune modification'}
                      >
                        {tasksSaving ? 'Enregistrement...' : 'Enregistrer'}
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className={item.id === 'gouv-3' ? 'mt-2' : 'space-y-6 mt-6'}>
                {item.id === 'gouv-3' ? (
                  <div className="px-2 pb-2">
                    {tasksError && (
                      <div className="mb-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                        {tasksError}
                      </div>
                    )}
                    {canEditTasks && tasksEditMode && (
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <button
                          type="button"
                          onClick={addEmptyRow}
                          className="inline-flex h-8 items-center justify-center rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
                        >
                          + Ajouter une ligne
                        </button>
                        {dirty && (
                          <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-full">
                            Modifications non enregistrées
                          </span>
                        )}
                      </div>
                    )}

                    {tasksLoading ? (
                      <div className="py-10 text-center text-sm text-slate-500">Chargement du tableau...</div>
                    ) : (
                      <div className="overflow-auto rounded-xl border border-slate-200 bg-white">
                        <table className="min-w-[1200px] w-full border-collapse text-[12px]">
                          <thead>
                            <tr className="bg-white">
                              {taskColumns.map((col) => (
                                <th
                                  key={col.key}
                                  className="sticky top-0 z-[1] whitespace-nowrap border-b border-slate-200 border-r border-slate-200 last:border-r-0 px-3 py-2 text-left text-[11px] font-bold text-slate-700 shadow-[inset_0_-1px_0_rgba(226,232,240,1)]"
                                >
                                  {col.label}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {(taskRows || []).map((row, rowIdx) => (
                              <tr key={rowIdx} className={rowIdx % 2 === 1 ? 'bg-slate-50/60' : 'bg-white'}>
                                {taskColumns.map((col) => {
                                  const value = String((row as any)?.[col.key] ?? '');
                                  return (
                                    <td key={col.key} className="border-b border-slate-100 border-r border-slate-200 last:border-r-0 px-3 py-2 align-top text-slate-700">
                                      {canEditTasks && tasksEditMode ? (
                                        <input
                                          value={value}
                                          onChange={(e) => onCellChange(rowIdx, col.key as any, e.target.value)}
                                          className="w-full min-w-[120px] rounded-md border border-slate-200 bg-white px-2 py-1 text-[12px] text-slate-800 focus:border-indigo-400 focus:outline-none focus:ring-1 focus:ring-indigo-200"
                                        />
                                      ) : (
                                        <span className="whitespace-pre-wrap">{value}</span>
                                      )}
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                            {(!taskRows || taskRows.length === 0) && (
                              <tr>
                                <td colSpan={taskColumns.length} className="px-4 py-10 text-center text-sm text-slate-500">
                                  Aucun élément.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ) : null}
                {item.points.map((point: Point) => {
                  // Un seul tableau pour gouv-3 : données API + mode édition (pas de 2e tableau HTML depuis les données)
                  if (item.id === 'gouv-3') {
                    return null;
                  }

                  const html = point.contenu.replace(/\n/g, '<br />');

                  return (
                    <div key={point.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                      <h4 className="text-lg font-semibold text-slate-900 mb-3">{point.titre}</h4>
                      <div
                        className="prose prose-slate max-w-none"
                        dangerouslySetInnerHTML={{ __html: html }}
                      />
                    {point.concepts && point.concepts.length > 0 && (
                      <div className="mt-4">
                        <p className="text-sm text-gray-500 mb-2">Concepts clés:</p>
                        <div className="flex flex-wrap gap-2">
                          {point.concepts.map((concept: string) => (
                            <span 
                              key={concept} 
                              className="bg-indigo-50 text-indigo-700 px-2.5 py-1 rounded-full text-xs font-semibold"
                            >
                              {concept}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    </div>
                  );
                })}
              </div>

              {index < communication.ordreDuJour.length - 1 && (
                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    className="inline-flex items-center justify-center rounded-md text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 h-10 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={() => setActiveTab(index + 1)}
                  >
                    Section suivante
                  </button>
                </div>
              )}
            </TabPanel>
          ))}
        </TabGroup>
      </div>
    </Modal>
  );
}